import { useCallback, useMemo, useState, type ReactNode } from 'react';

import { AnnouncerContext, type AnnouncerApi, type Politeness } from './announcerContext';

interface AnnouncerProviderProps {
  children: ReactNode;
}

/** Zero-width space, written as an escape: an invisible literal is unreviewable. */
const ZERO_WIDTH = '\u200B';

/**
 * Mounts the two live regions and provides `announce`.
 *
 * The regions are rendered ALWAYS, empty, from first paint. A live region added to
 * the DOM at the same moment as its text is frequently not announced at all —
 * assistive tech has to be observing the node before it changes. Hence two
 * permanently-mounted <p> elements whose text content we swap.
 *
 * Announcing the same string twice in a row is also silently dropped by most
 * screen readers (nothing "changed"), which breaks "3 issues found" after a
 * re-validation that returns the same count. Appending an invisible zero-width
 * space forces a real text change without altering what is spoken.
 */
export function AnnouncerProvider({ children }: AnnouncerProviderProps) {
  const [polite, setPolite] = useState('');
  const [assertive, setAssertive] = useState('');

  const announce = useCallback((message: string, politeness: Politeness = 'polite') => {
    const trimmed = message.trim();
    if (!trimmed) return;
    const setter = politeness === 'assertive' ? setAssertive : setPolite;
    setter((previous) => {
      const withoutMarkers = previous.replaceAll(ZERO_WIDTH, '');
      if (withoutMarkers !== trimmed) return trimmed;
      // Same words as last time: toggle the invisible marker so the text node
      // genuinely changes and the announcement fires again.
      return previous.endsWith(ZERO_WIDTH) ? trimmed : `${trimmed}${ZERO_WIDTH}`;
    });
  }, []);

  const clear = useCallback(() => {
    setPolite('');
    setAssertive('');
  }, []);

  const value = useMemo<AnnouncerApi>(() => ({ announce, clear }), [announce, clear]);

  return (
    <AnnouncerContext.Provider value={value}>
      {children}
      {/* Visually hidden, permanently mounted, never conditionally rendered. */}
      <div className="sr-only">
        <p aria-live="polite" aria-atomic="true" data-testid="announcer-polite">
          {polite}
        </p>
        <p aria-live="assertive" aria-atomic="true" data-testid="announcer-assertive">
          {assertive}
        </p>
      </div>
    </AnnouncerContext.Provider>
  );
}
